/**
 * useTotalCost Hook
 * Fetches and calculates total cost from base model and selected products
 *
 * @package
 */

import { useState, useEffect } from '@wordpress/element';
import { cachedApiFetch } from './useApiCache';
import { getProductLine } from './priceHelpers';

/**
 * Custom hook to fetch and calculate total cost.
 *
 * Price objects { incVAT, exVAT } are stored in state; the displayed amounts are
 * derived reactively from vatMode so toggling inc/ex never requires a re-fetch.
 * Installation time is costed into each line at the configured hourly rate rather
 * than shown as a separate total, and scales with the line's quantity.
 *
 * @param {number|null} selectedBaseModel            - Base model (or variant) product ID
 * @param {Array}       selectedProducts             - Array of selected product IDs
 * @param {string}      vatMode                      - 'including' or 'excluding'
 * @param {Object}      [options]                    - Cost options
 * @param {number}      [options.workshopHourlyRate] - Hourly labour rate (excl. VAT)
 * @param {number}      [options.taxRate]            - VAT rate as a decimal (e.g. 0.25), applied to installation in inc-VAT mode
 * @param {Object}      [options.quantities]         - Product ID => quantity
 * @return {Object} Object containing total, baseModelPrice, productsPrice, installationHours, baseModelName, isLoading
 */
export const useTotalCost = ( selectedBaseModel, selectedProducts, vatMode = 'including', options = {} ) => {
	const {
		workshopHourlyRate = 0,
		taxRate = 0,
		quantities = {},
	} = options;
	const [ baseModelData, setBaseModelData ] = useState( null );
	const [ productsData, setProductsData ] = useState( [] );
	const [ baseModelName, setBaseModelName ] = useState( '' );
	const [ isLoading, setIsLoading ] = useState( true );

	// Fetch base model price + installation hours.
	useEffect( () => {
		if ( ! selectedBaseModel || selectedBaseModel <= 0 ) {
			setBaseModelData( null );
			setBaseModelName( '' );
			setIsLoading( false );
			return;
		}

		// `ignore` drops a superseded response, so a base model the customer has
		// already switched away from (e.g. to "no base model") can't land in the total.
		let ignore = false;

		setIsLoading( true );
		cachedApiFetch( `/wp/v2/qcom_product/${ selectedBaseModel }` )
			.then( data => {
				if ( ignore ) {
					return;
				}
				setBaseModelData( { id: selectedBaseModel, price: data?.price ?? null, workshop_hours: data?.workshop_hours ?? 0 } );
				setBaseModelName( data?.title?.rendered || '' );
				setIsLoading( false );
			} )
			.catch( () => {
				if ( ignore ) {
					return;
				}
				setBaseModelData( null );
				setBaseModelName( '' );
				setIsLoading( false );
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedBaseModel ] );

	// Fetch selected products' price + installation hours.
	useEffect( () => {
		if ( selectedProducts.length === 0 ) {
			setProductsData( [] );
			return;
		}

		let ignore = false;

		const promises = selectedProducts.map( productId =>
			cachedApiFetch( `/wp/v2/qcom_product/${ productId }` )
				.then( data => ( { id: productId, price: data?.price ?? null, workshop_hours: data?.workshop_hours ?? 0 } ) )
				.catch( () => null )
		);

		Promise.all( promises )
			.then( results => {
				if ( ! ignore ) {
					setProductsData( results );
				}
			} )
			.catch( () => {
				if ( ! ignore ) {
					setProductsData( [] );
				}
			} );

		return () => {
			ignore = true;
		};
	}, [ selectedProducts ] );

	// Derive displayed amounts from vatMode — no re-fetch needed on toggle.
	const lineOptions = { vatMode, hourlyRate: workshopHourlyRate, taxRate };
	const baseModelLine = getProductLine( baseModelData || {}, lineOptions );

	const productLines = productsData.map( item => getProductLine( item || {}, {
		...lineOptions,
		quantity: item ? quantities[ item.id ] : 1,
	} ) );

	const sum = ( lines, key ) => lines.reduce( ( carry, line ) => carry + line[ key ], 0 );

	const baseModelPrice = baseModelLine.total;
	const productsPrice = sum( productLines, 'total' );
	const installationHours = baseModelLine.installationHours + sum( productLines, 'installationHours' );

	return {
		total: baseModelPrice + productsPrice,
		baseModelPrice,
		productsPrice,
		installationHours,
		baseModelName,
		isLoading,
	};
};
